import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Meta } from 'src/components/Meta';
import { Stack } from 'src/components/Stack';
import { useOptionalSession } from 'src/contexts/SessionContext';

import { usePosContext } from './PosContext';
import { FindAccountDialog } from './components/FindAccountDialog';
import { PosCart } from './components/PosCart';
import PosPinDialog from './components/PosPinDialog';
import { PosProductListFilter } from './components/PosPorductListFilter';
import { PosProductList } from './components/PosProductList';
import { PosProductListPagination } from './components/PosProductListPagination';
import { PosTopBar } from './components/PosTopBar';
import ProcessingPaymentDialog from './components/ProcessingPaymentDialog';
import { SelectTerminalDialog } from './components/SelectTerminalDialog';
import { WelcomeDialog } from './components/WelcomeDialog';

export default function PosLayout() {
  const {
    config,
    showWelcomeDialog,
    setShowWelcomeDialog,
    showFindAccountDialog,
    setShowFindAccountDialog,
    setSelectedUser,
    selectedUser,
    setSelectedTerminal,
    shouldShowTerminalDialog,
    setShouldShowTerminalDialog,
    isProcessingPayment,
    handleGuestUser,
    // PIN-related state
    isPinValidated,
    showPinDialog,
    validatePin,
    isValidatingPin,
    // Setup Payment PIN validation state
    setupPaymentPin,
    isSettingUpPaymentPin,
    // Payment PIN validation state
    showPaymentPinDialog,
    setShowPaymentPinDialog,
    validatePaymentPin,
    isValidatingPaymentPin,
    guestCheckoutEnabled,
    posType,
    posLocation,
    // Kiosk staff PIN validation state
    isKioskStaffPinValidated,
    showKioskStaffPinDialog,
    validateKioskStaffPin,
    isValidatingKioskStaffPin,
  } = usePosContext();
  const { session } = useOptionalSession();
  const navigate = useNavigate();

  // Check if PIN is required for this configuration type
  const requiresPosPin = useMemo(
    () => config?.posType === 'pos' && !isPinValidated,
    [config?.posType, isPinValidated]
  );

  // Check if kiosk staff PIN is required for kiosk configurations
  const requiresKioskStaffPin = useMemo(
    () => config?.posType === 'kiosk' && !isKioskStaffPinValidated,
    [config?.posType, isKioskStaffPinValidated]
  );

  // Block access to POS/Kiosk interface until PIN is validated
  const shouldBlockAccess = requiresPosPin || requiresKioskStaffPin;

  // Consolidated dialog state - determine which dialog type to show
  const pinDialogState = useMemo(() => {
    // POS PIN dialog
    if (showPinDialog && config?.posType === 'pos') {
      return {
        open: true,
        onSubmit: validatePin,
        isSubmitting: isValidatingPin,
        kioskStaffMode: false,
        kioskMode: false,
        setupPin: false,
        paymentPin: false,
        onClose: undefined,
      };
    }

    // Kiosk Staff PIN dialog
    if (showKioskStaffPinDialog && config?.posType === 'kiosk') {
      return {
        open: true,
        onSubmit: validateKioskStaffPin,
        isSubmitting: isValidatingKioskStaffPin,
        kioskStaffMode: true,
        kioskMode: false,
        setupPin: false,
        paymentPin: false,
        onClose: undefined,
      };
    }

    // Payment PIN dialog
    if (showPaymentPinDialog) {
      const isKioskMode = config?.posType === 'kiosk';
      const isSetupMode = !selectedUser?.kioskPinRegistered;

      return {
        open: true,
        onSubmit: isSetupMode
          ? (pin: string, password?: string) =>
              setupPaymentPin({ pin, password })
          : validatePaymentPin,
        isSubmitting: isSetupMode
          ? isSettingUpPaymentPin
          : isValidatingPaymentPin,
        kioskStaffMode: false,
        kioskMode: isKioskMode,
        setupPin: isSetupMode,
        paymentPin: true,
        onClose: () => setShowPaymentPinDialog(false),
      };
    }

    // No dialog to show
    return {
      open: false,
      onSubmit: () => {},
      isSubmitting: false,
      kioskStaffMode: false,
      kioskMode: false,
      setupPin: false,
      paymentPin: false,
      onClose: undefined,
    };
  }, [
    showPinDialog,
    showKioskStaffPinDialog,
    showPaymentPinDialog,
    config?.posType,
    selectedUser?.kioskPinRegistered,
    validatePin,
    validateKioskStaffPin,
    validatePaymentPin,
    setupPaymentPin,
    isValidatingPin,
    isValidatingKioskStaffPin,
    isValidatingPaymentPin,
    isSettingUpPaymentPin,
    setShowPaymentPinDialog,
  ]);

  return (
    <>
      <Meta title="Point of Sale" brand={session?.brand} />
      <Stack>
        <PosTopBar />
        {!shouldBlockAccess ? (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            height={{ xs: 'calc(100vh - 64px)', md: 'calc(100vh - 64px)' }}
            p={2}
            spacing={1}
            justifyContent="space-between"
          >
            <Stack
              alignItems="center"
              flex={1}
              spacing={2}
              height={{
                xs: '30vh',
                md: '100%',
              }}
              sx={{
                flex: {
                  xs: 12,
                  md: 3,
                },
              }}
            >
              <PosProductListFilter />

              <PosProductList />

              <PosProductListPagination />
            </Stack>

            <PosCart />
          </Stack>
        ) : (
          <Stack
            height={{ xs: 'calc(100vh - 64px)', md: 'calc(100vh - 64px)' }}
            justifyContent="center"
            alignItems="center"
            p={4}
          >
            {/* Content blocked until PIN is validated */}
          </Stack>
        )}

        <PosPinDialog
          open={pinDialogState.open}
          onSubmit={pinDialogState.onSubmit}
          isSubmitting={pinDialogState.isSubmitting}
          kioskStaffMode={pinDialogState.kioskStaffMode}
          kioskMode={pinDialogState.kioskMode}
          setupPin={pinDialogState.setupPin}
          paymentPin={pinDialogState.paymentPin}
          onClose={pinDialogState.onClose}
        />

        {/* Find Account or continue as guest dialog */}
        <WelcomeDialog
          open={
            showWelcomeDialog && !shouldShowTerminalDialog && !shouldBlockAccess
          }
          onFindAccount={() => {
            setShowWelcomeDialog(false);
            setShowFindAccountDialog(true);
          }}
          onContinueAsGuest={() => {
            handleGuestUser();
            setShowWelcomeDialog(false);
            // Don't reset the terminal - it's already selected from the URL
          }}
        />
        {/* Find Account dialog */}
        <FindAccountDialog
          open={showFindAccountDialog && !shouldBlockAccess}
          onClose={
            guestCheckoutEnabled
              ? () => {
                  setShowFindAccountDialog(false);
                  // Only show welcome dialog if guest checkout is enabled
                  if (posType === 'kiosk') {
                    setShowWelcomeDialog(true);
                  }
                }
              : undefined
          }
          onSelectUser={(user) => {
            setSelectedUser(user);
            setShowFindAccountDialog(false);
            setShowWelcomeDialog(false);
            // Don't reset the terminal - it's already selected from the URL
          }}
        />
        {/* Select Terminal dialog */}
        <SelectTerminalDialog
          // open={!selectedTerminal?.id && !showWelcomeDialog && !showFindAccountDialog}
          open={shouldShowTerminalDialog && !shouldBlockAccess}
          onSelect={(terminal) => {
            setSelectedTerminal(terminal);
            setShouldShowTerminalDialog(false);
            navigate(
              `/${posType === 'kiosk' ? 'kiosk' : 'pos'}/${posLocation?.id}/${
                config?.id
              }/${terminal.id}`
            );
          }}
        />
        {isProcessingPayment && (
          <ProcessingPaymentDialog open={isProcessingPayment} />
        )}
      </Stack>
    </>
  );
}
